import React, { useCallback, useEffect, useState } from 'react';
import InboxTabs from '../../components/admin/InboxTabs';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Mail, RefreshCw, Search } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { useInquiriesApi, STATUS_META, NOTIFY_META, fmtDateTime } from '../../components/admin/inquiries/useInquiriesApi';
import { errMsg } from '../../components/admin/hiring/useHiringApi';

const FILTERS = [['', 'All'], ['new', 'New'], ['in_progress', 'In Progress'], ['resolved', 'Resolved']];

const StatusPill = ({ status }) => {
  const m = STATUS_META[status] || STATUS_META.new;
  return <span className={`text-[11px] font-bold uppercase tracking-wide px-2 py-0.5 rounded ${m.tone}`}>{m.label}</span>;
};

const InquiryRow = ({ i }) => {
  const n = NOTIFY_META[i.notification_status] || NOTIFY_META.pending;
  return (
    <Link to={`/admin/inquiries/${i.id}`} className="block px-4 py-3 border-t border-zinc-100 hover:bg-orange-50/40" data-testid={`inquiry-row-${i.id}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="font-semibold text-sm text-zinc-900 truncate">{i.name} <span className="font-normal text-zinc-500">· {i.email}</span></p>
          <p className="text-xs text-zinc-600 truncate mt-0.5">{i.preview}</p>
        </div>
        <div className="text-right shrink-0 space-y-1">
          <StatusPill status={i.status} />
          <p className="text-[11px] text-zinc-400">{fmtDateTime(i.created_at)}</p>
        </div>
      </div>
      <div className="flex gap-3 mt-1 text-[11px]">
        <span className="text-zinc-500">{i.assignee_name ? `Assigned: ${i.assignee_name}` : 'Unassigned'}</span>
        <span className={n.tone}>{n.label}</span>
        {i.lead_id && <span className="text-sky-700">Moved to leads</span>}
      </div>
    </Link>
  );
};

export default function AdminInquiriesPage() {
  const api = useInquiriesApi();
  const [params, setParams] = useSearchParams();
  const status = params.get('status') || '';
  const [q, setQ] = useState('');
  const [items, setItems] = useState(null);
  const [stats, setStats] = useState(null);

  const load = useCallback(async () => {
    try {
      const [list, s] = await Promise.all([api.list({ status, q }), api.stats()]);
      setItems(list); setStats(s);
    } catch (e) { toast.error(errMsg(e, 'Could not load inquiries')); setItems([]); }
  }, [api, status, q]);

  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);

  return (
    <>
      <InboxTabs />
      <div className="min-h-screen" data-testid="admin-inquiries-page">
        <header className="bg-white border-b border-zinc-200 sticky top-0 z-40">
          <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16 gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <Link to="/admin/dashboard" className="text-zinc-500 hover:text-zinc-900" data-testid="back-to-dashboard"><ArrowLeft className="w-5 h-5" /></Link>
              <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center"><Mail className="w-5 h-5 text-orange-600" /></div>
              <div className="min-w-0">
                <h1 className="text-lg font-bold text-zinc-900">Inquiries</h1>
                <p className="text-xs text-zinc-500 truncate" data-testid="inquiries-summary">Contact Us messages · {stats?.new ?? '—'} new{stats?.notification_failed ? ` · ${stats.notification_failed} email notification${stats.notification_failed === 1 ? '' : 's'} not delivered` : ''}</p>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={load} data-testid="inquiries-refresh"><RefreshCw className="w-4 h-4 mr-1" /> Refresh</Button>
          </div>
        </header>
        <main className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-4">
          <div className="flex flex-wrap gap-2 items-center">
            <div className="relative flex-1 min-w-[200px] max-w-sm">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <Input className="pl-9" placeholder="Search name, email or message" value={q} onChange={(e) => setQ(e.target.value)} data-testid="inquiries-search" />
            </div>
            {FILTERS.map(([v, label]) => (
              <Button key={v || 'all'} size="sm" variant={status === v ? 'default' : 'outline'} className={status === v ? 'bg-zinc-900' : ''}
                onClick={() => setParams(v ? { status: v } : {})} data-testid={`inquiries-filter-${v || 'all'}`}>
                {label}{v && stats ? ` (${stats[v]})` : ''}
              </Button>
            ))}
          </div>
          <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden" data-testid="inquiries-list">
            {items === null && <p className="p-6 text-sm text-zinc-500">Loading…</p>}
            {items?.length === 0 && <p className="p-6 text-sm text-zinc-500" data-testid="inquiries-empty">No inquiries match this view.</p>}
            {items?.map((i) => <InquiryRow key={i.id} i={i} />)}
          </div>
        </main>
      </div>
    </>
  );
}
